'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { useChat } from '@/lib/hooks/useChat';
import { createClient } from '@/lib/supabase/client';
import { trackEvent } from '@/lib/analytics';
import { scoreLeadWithClaude, saveLeadScore } from '@/lib/leadScoring';
import { smsAdminNewLead, getAdminSMSPrefs } from '@/lib/sms';
import Link from 'next/link';

const EXCLUDED_PATHS = ['/guest-guide'];

const STORAGE_KEY = 'premier_chat_session';

const SYSTEM_PROMPT = `You are a friendly and knowledgeable assistant for Premier Property MGT, a premium short-term rental management and Airbnb cleaning company based in Panama City Beach, Florida.

Your role is to answer visitor questions accurately, qualify leads, and capture contact information naturally.

## About Premier Property MGT
- **Services**: Professional Airbnb cleaning & turnover services, full-service short-term rental management
- **Service Area**: Panama City Beach, FL and surrounding Gulf Coast area
- **Phone**: (855) 596-3948
- **Contact**: premierpropertymgt.net/contact

## Cleaning Services & Pricing
- **Standard Turnover Clean**: Between-guest cleaning, fresh linens, towel restocking, consumable replenishment — hotel-level 5-star quality
- **Deep Clean**: Thorough top-to-bottom cleaning for seasonal resets or new property onboarding
- **Pricing**: Based on property size (bedrooms/bathrooms). Typical range: $85–$250+ per turnover depending on property size. Custom quotes provided after property assessment.
- **Turnaround Time**: Same-day turnovers available. We coordinate with your booking calendar.

## Property Management Services
- **Management Fee**: 18–25% of gross rental revenue (transparent, no hidden fees)
- **What's included**: Dynamic pricing optimization, 24/7 guest communication, listing management on Airbnb/VRBO/Booking.com, full-service coordination, check-in/check-out management, maintenance coordination
- **Booking Process**: We handle everything — from listing creation to guest checkout inspections

## Hiring Cleaners
- We are always looking for reliable, detail-oriented cleaning professionals in the PCB area
- Applicants can apply at premierpropertymgt.net/cleaner-application
- We offer flexible scheduling, competitive pay, and a supportive team environment

## FAQ Answers
- **Do I need to be present for check-ins?** No — we handle all guest communication and check-in/check-out coordination
- **How do you maximize rental income?** Dynamic pricing tools + professional photography + optimized multi-platform listings + 24/7 guest support
- **What platforms do you list on?** Airbnb, VRBO, Booking.com, and more
- **How quickly do you respond?** Our team responds within 2 hours during business hours

## Lead Qualification
After answering 2–3 questions, naturally ask for the visitor's name, property size (number of bedrooms), and email address so our team can follow up with a personalized quote. Be conversational and friendly — don't make it feel like a form. Say something like: "I'd love to connect you with our team for a personalized quote — could I get your name and email?"

Once you have their name AND email, include this exact JSON at the END of your response (after your conversational text), on its own line:
LEAD_CAPTURED:{"name":"[name]","email":"[email]","bedrooms":"[bedrooms or empty string]"}

## Tone
- Warm, professional, and knowledgeable
- Use "we" and "our team" — you represent Premier Property
- Keep responses concise (2–4 sentences max unless explaining pricing)
- Never make up specific prices — give ranges and offer a custom quote`;

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
}

interface CapturedLead {
  name: string;
  email: string;
  bedrooms: string;
}

interface StoredSession {
  messages: ChatMessage[];
  sessionId: string;
  leadCaptured: boolean;
}

function generateSessionId(): string {
  return `chat_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function AIChatWidget() {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sessionInitialized = useRef(false);

  const { response, isLoading, error, sendMessage } = useChat(
    'ANTHROPIC',
    'claude-haiku-4-5',
    true
  );

  // Load session from localStorage
  useEffect(() => {
    if (sessionInitialized.current) return;
    sessionInitialized.current = true;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed: StoredSession = JSON.parse(stored);
        setMessages(parsed.messages || []);
        setSessionId(parsed.sessionId || generateSessionId());
        setLeadCaptured(parsed.leadCaptured || false);
      } else {
        const newSessionId = generateSessionId();
        setSessionId(newSessionId);
        // Welcome message
        const welcome: ChatMessage = {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          content: "Hi! 👋 I'm the Premier Property assistant. I can answer questions about our Airbnb cleaning services, property management, pricing, and more. What can I help you with today?",
          timestamp: Date.now(),
        };
        setMessages([welcome]);
      }
    } catch {
      const newSessionId = generateSessionId();
      setSessionId(newSessionId);
    }
  }, []);

  // Persist session to localStorage
  useEffect(() => {
    if (!sessionId || messages.length === 0) return;
    try {
      const session: StoredSession = { messages, sessionId, leadCaptured };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch {
      // localStorage unavailable
    }
  }, [messages, sessionId, leadCaptured]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setHasUnread(false);
    }
  }, [isOpen]);

  // Handle streaming response completion
  const lastResponseRef = useRef('');
  useEffect(() => {
    if (!isLoading && response && response !== lastResponseRef.current) {
      lastResponseRef.current = response;

      // Check for lead capture signal
      const leadMatch = response.match(/LEAD_CAPTURED:(\{[^}]+\})/);
      let cleanContent = response.replace(/LEAD_CAPTURED:\{[^}]+\}/, '').trim();

      const assistantMsg: ChatMessage = {
        id: `msg_${Date.now()}`,
        role: 'assistant',
        content: cleanContent,
        timestamp: Date.now(),
      };

      setMessages(prev => {
        // Replace streaming placeholder if exists
        const withoutStreaming = prev.filter(m => m.id !== 'streaming');
        return [...withoutStreaming, assistantMsg];
      });

      if (!isOpen) setHasUnread(true);

      // Process lead capture
      if (leadMatch && !leadCaptured) {
        try {
          const leadData: CapturedLead = JSON.parse(leadMatch[1]);
          if (leadData.name && leadData.email) {
            handleLeadCapture(leadData);
          }
        } catch {
          // Invalid JSON in lead capture
        }
      }
    }
  }, [isLoading, response]);

  // Show streaming message in real-time
  useEffect(() => {
    if (isLoading && response) {
      const cleanContent = response.replace(/LEAD_CAPTURED:\{[^}]+\}/, '').trim();
      setMessages(prev => {
        const withoutStreaming = prev.filter(m => m.id !== 'streaming');
        return [...withoutStreaming, {
          id: 'streaming',
          role: 'assistant',
          content: cleanContent,
          timestamp: Date.now(),
        }];
      });
    }
  }, [isLoading, response]);

  const handleLeadCapture = useCallback(async (lead: CapturedLead) => {
    if (leadCaptured || savingLead) return;
    setSavingLead(true);

    try {
      const supabase = createClient();
      const transcript = messages
        .filter(m => m.id !== 'streaming')
        .map(m => `${m.role === 'user' ? 'Visitor' : 'Assistant'}: ${m.content}`)
        .join('\n');

      const { data: insertedLead, error: dbError } = await supabase
        .from('leads')
        .insert({
          name: lead.name,
          email: lead.email,
          phone: '',
          service_type: 'AI Chat Inquiry',
          bedrooms: lead.bedrooms || '',
          message: `Chat transcript:\n${transcript}`,
          lead_status: 'new',
          source: 'ai_chat',
        })
        .select('id')
        .single();

      if (!dbError && insertedLead?.id) {
        const leadId = insertedLead.id;
        setLeadCaptured(true);

        // GA4 event
        trackEvent('chat_lead_captured', {
          session_id: sessionId,
          lead_name: lead.name,
          bedrooms: lead.bedrooms,
        });

        // Score lead + send notifications (non-blocking)
        const leadDataForScoring = {
          name: lead.name,
          phone: '',
          serviceType: 'AI Chat Inquiry',
          bedrooms: lead.bedrooms || '',
          message: `Chat lead from AI widget. Transcript:\n${transcript.slice(0, 500)}`,
        };

        getAdminSMSPrefs().then(adminPrefs => {
          scoreLeadWithClaude(leadDataForScoring).then(score => {
            if (score) saveLeadScore(leadId, score);
            if (adminPrefs) {
              smsAdminNewLead({
                name: lead.name,
                phone: 'AI Chat',
                serviceType: 'AI Chat Inquiry',
                bedrooms: lead.bedrooms,
                urgencyScore: score?.urgency_score,
                adminUserId: adminPrefs.userId,
                adminPhone: adminPrefs.phone,
              });
            }
          }).catch(() => {
            if (adminPrefs) {
              smsAdminNewLead({
                name: lead.name,
                phone: 'AI Chat',
                serviceType: 'AI Chat Inquiry',
                bedrooms: lead.bedrooms,
                adminUserId: adminPrefs.userId,
                adminPhone: adminPrefs.phone,
              });
            }
          });
        }).catch(() => {});

        // Send admin email with transcript
        supabase.functions.invoke('send-email', {
          body: {
            type: 'contact_lead',
            data: {
              name: lead.name,
              email: lead.email,
              phone: 'AI Chat Widget',
              serviceType: 'AI Chat Inquiry',
              bedrooms: lead.bedrooms || 'Not specified',
              message: `AI Chat Lead Captured\n\nTranscript:\n${transcript}`,
            },
          },
        }).catch(() => {});
      }
    } catch (err) {
      console.warn('[AIChatWidget] Lead capture failed:', err);
    } finally {
      setSavingLead(false);
    }
  }, [leadCaptured, savingLead, messages, sessionId]);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    const userMsg: ChatMessage = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content: trimmed,
      timestamp: Date.now(),
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    lastResponseRef.current = '';

    // GA4 tracking
    trackEvent('chat_message_sent', {
      session_id: sessionId,
      message_count: messages.length + 1,
    });

    // Build messages array for API
    const apiMessages = [
      { role: 'system', content: SYSTEM_PROMPT },
      ...messages
        .filter(m => m.id !== 'streaming')
        .map(m => ({ role: m.role, content: m.content })),
      { role: 'user', content: trimmed },
    ];

    sendMessage(apiMessages, { temperature: 0.7, max_tokens: 600 });
  }, [input, isLoading, messages, sessionId, sendMessage]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setHasUnread(false);
    trackEvent('chat_opened', { session_id: sessionId });
  }, [sessionId]);

  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleTalkToHuman = useCallback(() => {
    trackEvent('chat_talk_to_human', { session_id: sessionId });
  }, [sessionId]);

  if (EXCLUDED_PATHS.some(p => pathname.startsWith(p))) return null;

  return (
    <>
      {/* Floating Chat Button */}
      <button
        onClick={isOpen ? handleClose : handleOpen}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2"
        style={{ backgroundColor: '#0a1628' }}
      >
        {hasUnread && !isOpen && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-gold rounded-full border-2 border-white" />
        )}
        {isOpen ? (
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="none" stroke="#c9a84c" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        )}
      </button>

      {/* Chat Drawer */}
      <div
        className={`fixed bottom-24 right-4 sm:right-6 z-50 flex flex-col transition-all duration-300 ${
          isOpen
            ? 'opacity-100 translate-y-0 pointer-events-auto' :'opacity-0 translate-y-4 pointer-events-none'
        }`}
        style={{
          width: 'min(calc(100vw - 2rem), 380px)',
          height: 'min(calc(100vh - 8rem), 560px)',
        }}
      >
        <div className="flex flex-col h-full rounded-2xl shadow-2xl overflow-hidden border border-gray-200" style={{ backgroundColor: '#ffffff' }}>
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 flex-shrink-0" style={{ backgroundColor: '#0a1628' }}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#c9a84c' }}>
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm leading-tight">Premier Property</p>
              <p className="text-xs leading-tight" style={{ color: '#c9a84c' }}>AI Assistant · Typically replies instantly</p>
            </div>
            <button
              onClick={handleClose}
              className="text-white/60 hover:text-white transition-colors p-1 rounded"
              aria-label="Close chat"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ backgroundColor: '#f8f9fa' }}>
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex flex-col ${
                  msg.role === 'user' ? 'items-end' : 'items-start'
                }`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                    msg.role === 'user' ?'text-white rounded-br-sm' :'text-gray-800 rounded-bl-sm border border-gray-200'
                  }`}
                  style={{
                    backgroundColor: msg.role === 'user' ? '#0a1628' : '#ffffff',
                  }}
                >
                  {msg.content}
                </div>
                <span className="text-xs text-gray-400 mt-0.5 px-1">
                  {formatTime(msg.timestamp)}
                </span>
              </div>
            ))}

            {/* Typing indicator */}
            {isLoading && !response && (
              <div className="flex items-start">
                <div className="rounded-2xl rounded-bl-sm px-4 py-3 border border-gray-200" style={{ backgroundColor: '#ffffff' }}>
                  <div className="flex gap-1 items-center h-4">
                    <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            {/* Lead captured confirmation */}
            {leadCaptured && (
              <div className="flex justify-center">
                <div className="text-xs text-center px-3 py-1.5 rounded-full" style={{ backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>
                  ✓ Connected with our team
                </div>
              </div>
            )}

            {error && (
              <div className="flex justify-center">
                <div className="text-xs text-center px-3 py-1.5 rounded-full" style={{ backgroundColor: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>
                  Connection issue — please try again
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Talk to Human Banner */}
          <div className="px-4 py-2 flex-shrink-0 border-t border-gray-100" style={{ backgroundColor: '#fffbf0' }}>
            <Link
              href="/contact"
              onClick={handleTalkToHuman}
              className="flex items-center justify-center gap-2 text-xs font-medium py-1.5 rounded-lg transition-colors hover:opacity-80"
              style={{ color: '#0a1628' }}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Talk to a Human
              <span className="text-gray-400">·</span>
              <span className="text-gray-500">(855) 596-3948</span>
            </Link>
          </div>

          {/* Input */}
          <div className="px-3 py-3 flex-shrink-0 border-t border-gray-200" style={{ backgroundColor: '#ffffff' }}>
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about pricing, services..."
                disabled={isLoading}
                className="flex-1 text-sm px-3 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-gray-400 bg-gray-50 disabled:opacity-60 transition-colors"
                maxLength={500}
              />
              <button
                onClick={handleSend}
                disabled={isLoading || !input.trim()}
                aria-label="Send message"
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-all disabled:opacity-40 hover:opacity-80"
                style={{ backgroundColor: '#0a1628' }}
              >
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
            <p className="text-center text-xs text-gray-400 mt-1.5">Powered by AI · Premier Property MGT</p>
          </div>
        </div>
      </div>
    </>
  );
}
